import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Box } from "@mui/material";
import ParentViewMap from "../../components/dashboard/ParentViewMap";
import { getVehicleDirection } from "../../components/dashboard/dashboardAPI";
import {
  nearestIndex,
  segmentDistance,
} from "../../components/stops/mobileStops/mobileScreenUtils";
import { useTranslation } from "react-i18next";
import config from "../../config.json";
import { styles } from "./ParentViewStyle";
import { determineVehicleDirection } from "../../utils/vehicleDirection.js";

// Constants for ETA calculation and journey phase detection
const CONSTANTS = {
  EARTH_RADIUS: 6371e3,
  AVG_SPEED: 30, // km/h
  MIN_STOP_TIME: 0.1,
  MAX_STOP_TIME: 100,
  TRAFFIC_MULTIPLIER: 1.1,
  STOP_RADIUS: 20, // meters - radius to detect if vehicle is at a stop
  ARRIVAL_DISTANCE: 70, // meters - show "Arrived" within this distance
  ARRIVAL_TIME: 10, // seconds - show "Arrived" if ETA is within this time
  PICKUP_COMPLETION_DISTANCE: 150, // meters - distance vehicle must be from pickup to mark as "picked up"
  DROP_COMPLETION_DISTANCE: 50, // ✅ CHANGED from 150 to 50 meters - vehicle must be THIS close to home
  PHASE_DWELL_TIME: 15000, // ms - time to wait at stop before transitioning phase (15 seconds)
  DIRECTION_CHANGE_THRESHOLD: 200, // meters - min distance change to detect direction reversal
  ROUTE_PROXIMITY_THRESHOLD: 200, // meters - vehicle must be within this distance to be considered "on route"
  MOVEMENT_THRESHOLD: 5, // ✅ ADDED - minimum movement in meters to detect direction change
};

// Journey Phases
const JOURNEY_PHASE = {
  GOING_TO_PICKUP: "GOING_TO_PICKUP", // Morning: Bus going to pickup location
  GOING_TO_SCHOOL: "GOING_TO_SCHOOL", // Morning: After pickup, going to school
  GOING_TO_DROP: "GOING_TO_DROP", // Afternoon: Coming back from school to drop location (home)
  STUDENT_DROPPED: "STUDENT_DROPPED", // After drop-off complete
  REACHED_SCHOOL: "REACHED_SCHOOL", // When student reaches school
  UNKNOWN: "UNKNOWN", // Cannot determine phase yet
};

const VALID_ROUTE_DIRECTIONS = new Set(["source_to_dest", "dest_to_source"]);

const ETA_CONSTANTS = {
  AVG_SPEED: 20,
  TRAFFIC_MULTIPLIER: 1.1,
  MIN_STOP_TIME: 1,
  MAX_STOP_TIME: 35,
  HISTORY_POINTS: 15,
  MIN_TIME_DIFF: 10,
  MAX_SPEED_THRESHOLD: 100,
  MIN_DISTANCE_THRESHOLD: 10,
  SPEED_UPDATE_INTERVAL: 300000,
  EMA_WEIGHT: 0.8,
};

const normalizeCoordinates = (position) => {
  if (!position) return null;

  const lat = parseFloat(position.lat ?? position.latitude);
  const lng = parseFloat(position.lng ?? position.longitude);

  if (Number.isNaN(lat) || Number.isNaN(lng)) {
    return null;
  }

  return { lat, lng };
};

const calculateDistance = (pos1, pos2) => {
  const point1 = normalizeCoordinates(pos1);
  const point2 = normalizeCoordinates(pos2);

  if (!point1 || !point2) return Infinity;

  const lat1 = (point1.lat * Math.PI) / 180;
  const lat2 = (point2.lat * Math.PI) / 180;
  const deltaLat = ((point2.lat - point1.lat) * Math.PI) / 180;
  const deltaLng = ((point2.lng - point1.lng) * Math.PI) / 180;

  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1) *
      Math.cos(lat2) *
      Math.sin(deltaLng / 2) *
      Math.sin(deltaLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return CONSTANTS.EARTH_RADIUS * c;
};

const sortRouteCoordinates = (routeCoordinates = []) =>
  [...routeCoordinates].sort((left, right) => {
    const leftOrder = Number(left?.order ?? 0);
    const rightOrder = Number(right?.order ?? 0);
    return leftOrder - rightOrder;
  });

const calculateRouteDistanceKm = (
  vehiclePosition,
  targetStop,
  routeCoordinates,
) => {
  const currentPoint = normalizeCoordinates(vehiclePosition);
  const targetPoint = normalizeCoordinates(targetStop);

  if (!currentPoint || !targetPoint || routeCoordinates.length < 2) {
    return null;
  }

  const vehicleNearestIdx = nearestIndex(currentPoint, routeCoordinates);
  const targetNearestIdx = nearestIndex(targetPoint, routeCoordinates);

  if (vehicleNearestIdx === -1 || targetNearestIdx === -1) {
    return null;
  }

  return segmentDistance(routeCoordinates, vehicleNearestIdx, targetNearestIdx);
};

// Check if vehicle is on the route path with hysteresis
const onRouteCountRef = { current: 0 };

const isVehicleOnRoute = (vehiclePosition, routeCoordinates) => {
  if (!vehiclePosition || !routeCoordinates || routeCoordinates.length === 0) {
    return false;
  }

  let minDistance = Infinity;

  for (const coord of routeCoordinates) {
    const distance = calculateDistance(vehiclePosition, coord);
    minDistance = Math.min(minDistance, distance);

    // Early exit if clearly on route
    if (distance < CONSTANTS.ROUTE_PROXIMITY_THRESHOLD) {
      onRouteCountRef.current = Math.min(onRouteCountRef.current + 1, 5);
      return true;
    }
  }

  // Add hysteresis: require 3 consecutive "off-route" readings
  if (minDistance >= CONSTANTS.ROUTE_PROXIMITY_THRESHOLD) {
    onRouteCountRef.current = Math.max(onRouteCountRef.current - 1, -3);
  }

  // Only consider "off-route" after 3 consecutive readings
  return onRouteCountRef.current > -3;
};

// FORMAT ETA AS REAL TIME (e.g., "1:30 PM")
const formatETAAsTime = (etaInSeconds) => {
  if (!etaInSeconds || etaInSeconds <= 0) return "---";

  const now = new Date();
  const arrivalTime = new Date(now.getTime() + etaInSeconds * 1000);

  let hours = arrivalTime.getHours();
  const minutes = arrivalTime.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12;
  hours = hours ? hours : 12; // Convert 0 to 12

  const minutesStr = minutes < 10 ? "0" + minutes : minutes;

  return `${hours}:${minutesStr} ${ampm}`;
};

const getDirectionFromJourneyPhase = (journeyPhase) => {
  switch (journeyPhase) {
    case JOURNEY_PHASE.GOING_TO_PICKUP:
    case JOURNEY_PHASE.GOING_TO_SCHOOL:
    case JOURNEY_PHASE.REACHED_SCHOOL:
      return "source_to_dest";
    case JOURNEY_PHASE.GOING_TO_DROP:
    case JOURNEY_PHASE.STUDENT_DROPPED:
      return "dest_to_source";
    default:
      return "unknown";
  }
};

const resolveVehicleRouteDirection = ({
  apiDirection = "unknown",
  journeyPhase = JOURNEY_PHASE.UNKNOWN,
  currentVehiclePosition = null,
  pickupStop = null,
  dropStop = null,
  previousPositions = [],
}) => {
  const phaseDirection = getDirectionFromJourneyPhase(journeyPhase);
  let clientVerifiedDirection = "unknown";

  if (currentVehiclePosition && pickupStop && dropStop) {
    const directionResult = determineVehicleDirection({
      currentPosition: currentVehiclePosition,
      previousPositions,
      stops: [pickupStop, dropStop],
      terminalThreshold: CONSTANTS.ARRIVAL_DISTANCE,
      movementThreshold: CONSTANTS.MOVEMENT_THRESHOLD,
      requiredConfidence: 0.6,
    });

    if (
      directionResult?.terminalReached ||
      directionResult?.confidence >= 0.6
    ) {
      clientVerifiedDirection = directionResult.isInbound
        ? "dest_to_source"
        : "source_to_dest";
    }
  }

  if (VALID_ROUTE_DIRECTIONS.has(apiDirection)) {
    if (
      VALID_ROUTE_DIRECTIONS.has(clientVerifiedDirection) &&
      clientVerifiedDirection !== apiDirection
    ) {
      return clientVerifiedDirection;
    }

    return apiDirection;
  }

  if (VALID_ROUTE_DIRECTIONS.has(clientVerifiedDirection)) {
    return clientVerifiedDirection;
  }

  if (VALID_ROUTE_DIRECTIONS.has(phaseDirection)) {
    return phaseDirection;
  }

  return "unknown";
};

export default function ParentView() {
  const { t } = useTranslation();
  const [mapStops, setMapStops] = useState([]);
  const [mapRoutes, setMapRoutes] = useState([]);
  const [mapVehicles, setMapVehicles] = useState([]);
  const [currentVehiclePosition, setCurrentVehiclePosition] = useState(null);
  const [displayVehiclePosition, setDisplayVehiclePosition] = useState(null);
  const [studentInfo, setStudentInfo] = useState({
    name: "Your Child",
    icon: null,
  });
  const [shouldShowVehicle, setShouldShowVehicle] = useState(true);

  // Journey phase tracking
  const [journeyPhase, setJourneyPhase] = useState(JOURNEY_PHASE.UNKNOWN);
  const [vehicleRouteDirection, setVehicleRouteDirection] = useState("unknown");
  const [isDirectionResolved, setIsDirectionResolved] = useState(false);
  const [directionHistory, setDirectionHistory] = useState([]);
  const previousDistanceRef = useRef({
    toPickup: null,
    toSchool: null,
  });

  // Enhanced state management
  const [isOnRoute, setIsOnRoute] = useState(false);
  const [lastValidPosition, setLastValidPosition] = useState(null);
  const [hasReceivedAnyData, setHasReceivedAnyData] = useState(false);
  const vehicleSpeedHistoryRef = useRef({});
  const historicalAvgSpeedsRef = useRef({});
  const previousETAsRef = useRef({});

  const effectiveVehicleRouteDirection = useMemo(() => {
    const pickupStop = mapStops.find((stop) => stop.type === "pickup");
    const dropStop = mapStops.find((stop) => stop.type === "drop");

    return resolveVehicleRouteDirection({
      apiDirection: vehicleRouteDirection,
      journeyPhase,
      currentVehiclePosition,
      pickupStop,
      dropStop,
      previousPositions: directionHistory,
    });
  }, [
    currentVehiclePosition,
    directionHistory,
    journeyPhase,
    mapStops,
    vehicleRouteDirection,
  ]);

  const hasResolvedRouteDirection = VALID_ROUTE_DIRECTIONS.has(
    effectiveVehicleRouteDirection,
  );
  const primaryVehicleDeviceId = mapVehicles[0]?.device_id;
  const sortedRouteCoordinates = useMemo(
    () => sortRouteCoordinates(mapRoutes[0]?.routeCoordinates || []),
    [mapRoutes],
  );

  useEffect(() => {
    fetchParentViewData();
  }, []);

  useEffect(() => {
    if (!currentVehiclePosition) {
      setDirectionHistory([]);
      return;
    }

    setDirectionHistory((prev) => {
      const lastPosition = prev[prev.length - 1];

      if (
        lastPosition &&
        calculateDistance(lastPosition, currentVehiclePosition) <=
          CONSTANTS.MOVEMENT_THRESHOLD
      ) {
        return prev;
      }

      return [...prev, currentVehiclePosition].slice(-15);
    });
  }, [currentVehiclePosition]);

  useEffect(() => {
    if (!primaryVehicleDeviceId || !currentVehiclePosition) {
      return;
    }

    const normalizedPosition = normalizeCoordinates(currentVehiclePosition);
    if (!normalizedPosition) {
      return;
    }

    const history = vehicleSpeedHistoryRef.current[primaryVehicleDeviceId] || [];
    const lastPosition = history[history.length - 1];

    if (
      lastPosition &&
      calculateDistance(lastPosition, normalizedPosition) <=
        CONSTANTS.MOVEMENT_THRESHOLD
    ) {
      return;
    }

    vehicleSpeedHistoryRef.current[primaryVehicleDeviceId] = [
      ...history,
      {
        ...normalizedPosition,
        timestamp: Date.now(),
      },
    ].slice(-ETA_CONSTANTS.HISTORY_POINTS);
  }, [currentVehiclePosition, primaryVehicleDeviceId]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const now = Date.now();

      Object.keys(vehicleSpeedHistoryRef.current).forEach((deviceId) => {
        const history = vehicleSpeedHistoryRef.current[deviceId];

        if (!history || history.length < 2) {
          return;
        }

        let totalDistanceKm = 0;
        let totalTimeHours = 0;
        let validSegments = 0;

        for (let index = 1; index < history.length; index += 1) {
          const previousPoint = history[index - 1];
          const currentPoint = history[index];
          const timeDiffSeconds =
            (currentPoint.timestamp - previousPoint.timestamp) / 1000;

          if (timeDiffSeconds < ETA_CONSTANTS.MIN_TIME_DIFF) {
            continue;
          }

          const segmentDistanceMeters = calculateDistance(
            previousPoint,
            currentPoint,
          );

          if (segmentDistanceMeters < ETA_CONSTANTS.MIN_DISTANCE_THRESHOLD) {
            continue;
          }

          totalDistanceKm += segmentDistanceMeters / 1000;
          totalTimeHours += timeDiffSeconds / 3600;
          validSegments += 1;
        }

        if (
          validSegments >= Math.floor(ETA_CONSTANTS.HISTORY_POINTS / 2) &&
          totalTimeHours > 0
        ) {
          const averageSpeed = totalDistanceKm / totalTimeHours;

          if (
            averageSpeed >= 1 &&
            averageSpeed <= ETA_CONSTANTS.MAX_SPEED_THRESHOLD
          ) {
            historicalAvgSpeedsRef.current[deviceId] = {
              avgSpeed: averageSpeed,
              lastUpdated: now,
            };
          }
        }
      });
    }, ETA_CONSTANTS.SPEED_UPDATE_INTERVAL);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const deviceId = mapVehicles[0]?.device_id;
    if (!deviceId || !currentVehiclePosition) {
      setVehicleRouteDirection("unknown");
      setIsDirectionResolved(false);
      return;
    }

    let isActive = true;

    const fetchDirection = async () => {
      try {
        const direction = await getVehicleDirection(deviceId);
        if (!isActive) return;

        setVehicleRouteDirection(
          VALID_ROUTE_DIRECTIONS.has(direction) ? direction : "unknown",
        );
        setIsDirectionResolved(true);
      } catch (error) {
        console.error("Error fetching vehicle route direction:", error);
        if (isActive) {
          setVehicleRouteDirection("unknown");
          setIsDirectionResolved(true);
        }
      }
    };

    fetchDirection();
    const directionPoller = setInterval(fetchDirection, 15000);

    return () => {
      isActive = false;
      clearInterval(directionPoller);
    };
  }, [mapVehicles, currentVehiclePosition]);

  const fetchParentViewData = async () => {
    try {
      setMapStops([]);
      setMapRoutes([]);
      setMapVehicles([]);
      setCurrentVehiclePosition(null);
      setDisplayVehiclePosition(null);
      setVehicleRouteDirection("unknown");
      setIsDirectionResolved(false);
      setDirectionHistory([]);
      vehicleSpeedHistoryRef.current = {};
      historicalAvgSpeedsRef.current = {};
      previousETAsRef.current = {};
      setJourneyPhase(JOURNEY_PHASE.UNKNOWN);
      previousDistanceRef.current = {
        toPickup: null,
        toSchool: null,
      };

      const urlParams = new URLSearchParams(window.location.search);
      const parentId = urlParams.get("parentId") || "P00AR";

      const response = await fetch(
        `${config.baseURL}/parent/tracking/${parentId}`,
      );
      const data = await response.json();

      console.log("API Response:", data);

      if (data.students && data.students.length > 0) {
        const student = data.students[0];

        const profilePicDoc = student.documents?.find(
          (doc) => doc.name === "StudentImage",
        );

        const profilePictureUrl = profilePicDoc
          ? `${config.baseURL}/upload/image?fileName=${profilePicDoc.location}`
          : null;

        setStudentInfo({
          name: `${student.firstName} ${student.lastName}`,
          icon: profilePictureUrl,
        });

        const stops = [];

        if (student.pickupStop) {
          stops.push({
            id: student.pickupStop.id,
            name: student.pickupStop.name,
            latitude: student.pickupStop.latitude,
            longitude: student.pickupStop.longitude,
            stopId: student.pickupStop.stopId,
            isStop: true,
            type: "pickup",
          });
        }

        if (student.dropStop) {
          stops.push({
            id: student.dropStop.id,
            name: student.dropStop.name,
            latitude: student.dropStop.latitude,
            longitude: student.dropStop.longitude,
            stopId: student.dropStop.stopId,
            isStop: true,
            type: "drop",
          });
        }

        setMapStops(stops);

        if (student.route) {
          const route = {
            id: student.route.id,
            routeId: student.route.routeId,
            name: student.route.name,
            routeType: student.route.routeType,
            routeColor: student.route.routeColor || "#08ff03",
            routeCoordinates: student.route.routeCoordinates.map((coord) => ({
              id: coord.id,
              latitude: coord.latitude,
              longitude: coord.longitude,
              order: coord.order,
              isStop: stops.some(
                (stop) =>
                  stop.latitude === coord.latitude &&
                  stop.longitude === coord.longitude,
              ),
            })),
            pickupStopId: student.pickupStop?.stopId,
            dropStopId: student.dropStop?.stopId,
          };

          setMapRoutes([route]);
        }

        if (student.vehicle) {
          const vehicle = {
            id: student.vehicle.id,
            device_id: student.vehicle.device_id,
            name: student.vehicle.name,
            latitude: student.vehicle.latitude || "0",
            longitude: student.vehicle.longitude || "0",
            routeId: student.routeId,
            status: student.vehicle.status,
            vehicle_color: student.vehicle.vehicle_color || "#ff0000",
            VehicleNumber: student.vehicle.VehicleNumber,
            heading: 0,
          };

          if (student.vehicle.driver) {
            vehicle.drivers = [
              {
                id: student.vehicle.driver.id,
                name: `${student.vehicle.driver.first_name} ${student.vehicle.driver.last_name}`,
                phone: student.vehicle.driver.phone_number,
                role: "Driver",
              },
            ];
          }

          if (student.employee) {
            if (!vehicle.drivers) vehicle.drivers = [];
            vehicle.drivers.push({
              id: student.employee.id,
              name: `${student.employee.firstName} ${student.employee.lastName}`,
              phone: student.employee.phoneNumber,
              role: "Attendant",
            });
          }

          setMapVehicles([vehicle]);
        }
      }
    } catch (error) {
      console.error("Error fetching parent view data:", error);
    }
  };

  const calculateParentETA = useCallback((distanceKm, deviceId) => {
    if (!distanceKm || distanceKm <= 0) {
      return { smoothedETA: 0, etaSeconds: 0 };
    }

    const etaCacheKey = deviceId || "parent-view";
    const history = vehicleSpeedHistoryRef.current[etaCacheKey] || [];
    let realTimeSpeed = null;

    if (history.length >= 2) {
      const lastPoint = history[history.length - 1];
      const previousPoint = history[history.length - 2];
      const timeDiffSeconds =
        (lastPoint.timestamp - previousPoint.timestamp) / 1000;

      if (timeDiffSeconds >= ETA_CONSTANTS.MIN_TIME_DIFF) {
        const distanceMovedMeters = calculateDistance(previousPoint, lastPoint);

        if (distanceMovedMeters >= ETA_CONSTANTS.MIN_DISTANCE_THRESHOLD) {
          const speedKph =
            (distanceMovedMeters / 1000) / (timeDiffSeconds / 3600);

          if (
            speedKph > 0 &&
            speedKph <= ETA_CONSTANTS.MAX_SPEED_THRESHOLD
          ) {
            realTimeSpeed = speedKph;
          }
        }
      }
    }

    const historicalSpeed =
      historicalAvgSpeedsRef.current[etaCacheKey]?.avgSpeed ??
      ETA_CONSTANTS.AVG_SPEED;
    const effectiveSpeed = realTimeSpeed
      ? 0.7 * realTimeSpeed + 0.3 * historicalSpeed
      : historicalSpeed;

    let etaSeconds = (distanceKm / effectiveSpeed) * 3600;
    etaSeconds *= ETA_CONSTANTS.TRAFFIC_MULTIPLIER;
    etaSeconds += Math.floor((distanceKm * 1000) / 800) * 20;
    etaSeconds = Math.min(etaSeconds, ETA_CONSTANTS.MAX_STOP_TIME * 60);

    const currentEtaMinutes = Math.round(etaSeconds / 60);
    const previousEta =
      previousETAsRef.current[etaCacheKey] ?? currentEtaMinutes;
    const smoothedETA = Math.round(
      ETA_CONSTANTS.EMA_WEIGHT * currentEtaMinutes +
        (1 - ETA_CONSTANTS.EMA_WEIGHT) * previousEta,
    );

    previousETAsRef.current[etaCacheKey] = currentEtaMinutes;

    return {
      smoothedETA: Math.max(
        smoothedETA,
        ETA_CONSTANTS.MIN_STOP_TIME,
      ),
      etaSeconds,
    };
  }, []);

  // Check if vehicle is on route whenever position updates
  useEffect(() => {
    if (!currentVehiclePosition || !mapRoutes || mapRoutes.length === 0) {
      setIsOnRoute(false);
      return;
    }

    const route = mapRoutes[0];
    const onRoute = isVehicleOnRoute(
      currentVehiclePosition,
      route.routeCoordinates,
    );
    setIsOnRoute(onRoute);

    console.log(
      "Vehicle on route:",
      onRoute,
      "Position:",
      currentVehiclePosition,
    );
  }, [currentVehiclePosition, mapRoutes]);

  // 🧠 IMPROVED JOURNEY PHASE DETECTION
  useEffect(() => {
    if (!currentVehiclePosition || !mapStops || mapStops.length === 0) {
      return;
    }

    const pickupStop = mapStops.find((stop) => stop.type === "pickup");
    const dropStop = mapStops.find((stop) => stop.type === "drop");

    if (!pickupStop || !dropStop) return;

    const distanceToPickup = calculateDistance(
      currentVehiclePosition,
      pickupStop,
    );
    const distanceToSchool = calculateDistance(
      currentVehiclePosition,
      dropStop,
    );

    console.log("🔍 Phase Detection:", {
      currentPhase: journeyPhase,
      distanceToPickup: distanceToPickup?.toFixed(2) + "m",
      distanceToSchool: distanceToSchool?.toFixed(2) + "m",
      DROP_THRESHOLD: CONSTANTS.DROP_COMPLETION_DISTANCE + "m",
      apiDirection: vehicleRouteDirection,
      effectiveDirection: effectiveVehicleRouteDirection,
      currentPos: currentVehiclePosition,
      pickupPos: { lat: pickupStop.latitude, lng: pickupStop.longitude },
      schoolPos: { lat: dropStop.latitude, lng: dropStop.longitude },
    });

    if (hasResolvedRouteDirection) {
      if (effectiveVehicleRouteDirection === "source_to_dest") {
        setJourneyPhase(
          distanceToPickup <= CONSTANTS.PICKUP_COMPLETION_DISTANCE
            ? JOURNEY_PHASE.GOING_TO_PICKUP
            : distanceToSchool <= CONSTANTS.ARRIVAL_DISTANCE
              ? JOURNEY_PHASE.REACHED_SCHOOL
              : JOURNEY_PHASE.GOING_TO_SCHOOL,
        );
      } else {
        setJourneyPhase(
          distanceToPickup <= CONSTANTS.DROP_COMPLETION_DISTANCE
            ? JOURNEY_PHASE.STUDENT_DROPPED
            : JOURNEY_PHASE.GOING_TO_DROP,
        );
      }

      previousDistanceRef.current = {
        toPickup: distanceToPickup,
        toSchool: distanceToSchool,
      };
      return;
    }

    // Get previous distances to detect movement direction
    const prevDistToPickup = previousDistanceRef.current.toPickup;
    const prevDistToSchool = previousDistanceRef.current.toSchool;

    // 📍 PHASE DETECTION LOGIC
    if (prevDistToPickup !== null && prevDistToSchool !== null) {
      // ✅ IMPROVED: Use MOVEMENT_THRESHOLD instead of hardcoded 10
      const movingAwayFromPickup =
        distanceToPickup > prevDistToPickup + CONSTANTS.MOVEMENT_THRESHOLD;
      const movingTowardsSchool =
        distanceToSchool < prevDistToSchool - CONSTANTS.MOVEMENT_THRESHOLD;
      const movingAwayFromSchool =
        distanceToSchool > prevDistToSchool + CONSTANTS.MOVEMENT_THRESHOLD;
      const movingTowardsPickup =
        distanceToPickup < prevDistToPickup - CONSTANTS.MOVEMENT_THRESHOLD;

      console.log("🚗 Movement Detection:", {
        movingAwayFromPickup,
        movingTowardsSchool,
        movingAwayFromSchool,
        movingTowardsPickup,
        prevDistToPickup: prevDistToPickup?.toFixed(2),
        prevDistToSchool: prevDistToSchool?.toFixed(2),
      });

      // ✅ MORNING: Vehicle crossed pickup and heading to school
      if (
        journeyPhase === JOURNEY_PHASE.GOING_TO_PICKUP &&
        movingAwayFromPickup &&
        movingTowardsSchool &&
        distanceToPickup > CONSTANTS.PICKUP_COMPLETION_DISTANCE
      ) {
        console.log("🏫 Vehicle passed pickup, heading to school");
        setJourneyPhase(JOURNEY_PHASE.GOING_TO_SCHOOL);
        setShouldShowVehicle(true);
      }

      // ✅ MORNING: Vehicle reached school
      if (
        journeyPhase === JOURNEY_PHASE.GOING_TO_SCHOOL &&
        distanceToSchool <= CONSTANTS.ARRIVAL_DISTANCE
      ) {
        console.log("🏫 Student reached school");
        setJourneyPhase(JOURNEY_PHASE.REACHED_SCHOOL);
        setShouldShowVehicle(true);
      }

      // ✅ AFTERNOON: Vehicle leaving school, heading home (CRITICAL FIX)
      if (
        journeyPhase === JOURNEY_PHASE.UNKNOWN && 
        movingAwayFromSchool &&
        distanceToSchool > CONSTANTS.ARRIVAL_DISTANCE
      ) {
        console.log("🏠 Vehicle leaving school, heading home (GOING_TO_DROP)");
        setJourneyPhase(JOURNEY_PHASE.GOING_TO_DROP);
        setShouldShowVehicle(true);
      }

      // ✅ AFTERNOON: Vehicle arrived at home - STRICT 50m threshold
      if (
        journeyPhase === JOURNEY_PHASE.GOING_TO_DROP &&
        distanceToPickup <= CONSTANTS.DROP_COMPLETION_DISTANCE
      ) {
        console.log(
          "✅ Student dropped off at home (within " +
            CONSTANTS.DROP_COMPLETION_DISTANCE +
            "m)",
        );
        setJourneyPhase(JOURNEY_PHASE.STUDENT_DROPPED);
        setShouldShowVehicle(true);
      }
    }

    // ✅ IMPROVED: Initial phase detection - check if vehicle is CLOSE to pickup
    if (journeyPhase === JOURNEY_PHASE.UNKNOWN) {
      // If vehicle is very close to pickup point (within 200m), assume going to pickup
      if (distanceToPickup <= 200) {
        console.log(
          "🚏 Initial detection: Vehicle near pickup - Going to pickup",
        );
        setJourneyPhase(JOURNEY_PHASE.GOING_TO_PICKUP);
        setShouldShowVehicle(true);
      }
      else if (distanceToSchool <= CONSTANTS.ARRIVAL_DISTANCE) {
        console.log(
          "Vehicle near school on initial load, switching to school-to-home flow",
        );
        setJourneyPhase(JOURNEY_PHASE.GOING_TO_DROP);
        setShouldShowVehicle(true);
      }
      // If vehicle is far from pickup but closer to school, must be going to school
      else if (distanceToSchool < distanceToPickup) {
        console.log(
          "🏫 Initial detection: Vehicle closer to school - Going to school",
        );
        setJourneyPhase(JOURNEY_PHASE.GOING_TO_SCHOOL);
        setShouldShowVehicle(true);
      }
      // If vehicle is far from both, check which is closer
      else {
        console.log("🚏 Initial detection: Default - Going to pickup");
        setJourneyPhase(JOURNEY_PHASE.GOING_TO_PICKUP);
        setShouldShowVehicle(true);
      }
    }

    // If currently going to pickup but vehicle crossed it, switch to going to school
    if (journeyPhase === JOURNEY_PHASE.GOING_TO_PICKUP) {
      // If vehicle is now much farther from pickup than before AND moving towards school
      if (
        distanceToPickup > CONSTANTS.PICKUP_COMPLETION_DISTANCE &&
        distanceToSchool < distanceToPickup
      ) {
        console.log("🏫 Switched: Vehicle passed pickup - Now going to school");
        setJourneyPhase(JOURNEY_PHASE.GOING_TO_SCHOOL);
        setShouldShowVehicle(true);
      }
    }

    // Store previous distances for direction detection
    previousDistanceRef.current = {
      toPickup: distanceToPickup,
      toSchool: distanceToSchool,
    };
  }, [
    currentVehiclePosition,
    mapStops,
    journeyPhase,
    effectiveVehicleRouteDirection,
    hasResolvedRouteDirection,
    isDirectionResolved,
    vehicleRouteDirection,
  ]);

  // ✅ IMPROVED: Smart position display with state management
  useEffect(() => {
    if (!mapStops || mapStops.length === 0) {
      console.log("⚠️ No map stops available");
      return;
    }

    const pickupStop = mapStops.find((stop) => stop.type === "pickup");
    const dropStop = mapStops.find((stop) => stop.type === "drop");

    if (!pickupStop) {
      console.log("⚠️ No pickup stop found");
      return;
    }

    const pickupPosition = {
      lat: parseFloat(pickupStop.latitude),
      lng: parseFloat(pickupStop.longitude),
    };

    const dropPosition = dropStop
      ? {
          lat: parseFloat(dropStop.latitude),
          lng: parseFloat(dropStop.longitude),
        }
      : null;

    console.log("🔍 Display Logic Check:", {
      hasCurrentPosition: !!currentVehiclePosition,
      journeyPhase,
      isOnRoute,
      hasLastValid: !!lastValidPosition,
      hasReceivedData: hasReceivedAnyData,
    });

    // ✅ SCENARIO 1: Student already dropped off → ALWAYS show at pickup point
    if (journeyPhase === JOURNEY_PHASE.STUDENT_DROPPED) {
      console.log("✅ Student dropped, locked at pickup location");
      setDisplayVehiclePosition(pickupPosition);
      setShouldShowVehicle(true);
      return;
    }

    // ✅ SCENARIO 2: Student reached school → show at school
    if (journeyPhase === JOURNEY_PHASE.REACHED_SCHOOL && dropPosition) {
      console.log("🏫 Student reached school, showing at school");
      setDisplayVehiclePosition(dropPosition);
      setShouldShowVehicle(true);
      return;
    }

    // ✅ SCENARIO 3: Vehicle going to pickup → ALWAYS lock at pickup point
    if (journeyPhase === JOURNEY_PHASE.GOING_TO_PICKUP) {
      console.log("🚏 Vehicle going to pickup, locked at pickup point");
      setDisplayVehiclePosition(pickupPosition);
      setShouldShowVehicle(true); // ✅ Show the vehicle at pickup location
      return;
    }

    // ✅ PRIORITY 1: No data received yet → show at pickup
    if (!currentVehiclePosition && !hasReceivedAnyData) {
      console.log("🚏 No vehicle data yet, showing at pickup");
      setDisplayVehiclePosition(pickupPosition);
      setShouldShowVehicle(true);
      return;
    }

    // ✅ PRIORITY 2: Have received data at least once
    if (currentVehiclePosition) {
      setHasReceivedAnyData(true);

      if (!hasResolvedRouteDirection && !isDirectionResolved) {
        setLastValidPosition(currentVehiclePosition);
        setDisplayVehiclePosition(currentVehiclePosition);
        setShouldShowVehicle(true);
        return;
      }

      // Check if vehicle is between pickup and drop
      const distanceToPickup = calculateDistance(
        currentVehiclePosition,
        pickupPosition,
      );
      const distanceToDrop = dropPosition
        ? calculateDistance(currentVehiclePosition, dropPosition)
        : Infinity;

      const isNearPickup = distanceToPickup <= 150; // 150m radius
      const isNearDrop = distanceToDrop <= 150;

      console.log("📊 Distance Check:", {
        distanceToPickup: distanceToPickup?.toFixed(2),
        distanceToDrop: distanceToDrop?.toFixed(2),
        isNearPickup,
        isNearDrop,
        currentPos: currentVehiclePosition,
      });

      // ✅ SCENARIO 4: Going to drop (afternoon) - show real position until very close
      if (journeyPhase === JOURNEY_PHASE.GOING_TO_DROP) {
        // ✅ IMPROVED: Only lock to pickup when within DROP_COMPLETION_DISTANCE (50m)
        if (distanceToPickup <= CONSTANTS.DROP_COMPLETION_DISTANCE) {
          console.log(
            "🏠 Vehicle arrived at home (within 50m), locking at pickup point",
          );
          setDisplayVehiclePosition(pickupPosition);
          setShouldShowVehicle(true);
          return;
        }
        // Otherwise, show real position while approaching pickup
        console.log(
          "🚌 Vehicle on route to pickup (afternoon), showing real position",
        );
        setLastValidPosition(currentVehiclePosition);
        setDisplayVehiclePosition(currentVehiclePosition);
        setShouldShowVehicle(true);
        return;
      }

      // ✅ CRITICAL FIX: For GOING_TO_SCHOOL, ALWAYS show real-time position
      if (journeyPhase === JOURNEY_PHASE.GOING_TO_SCHOOL) {
        console.log(
          "🚌 Vehicle going to school, showing real-time position at:",
          currentVehiclePosition,
        );
        setLastValidPosition(currentVehiclePosition);
        setDisplayVehiclePosition(currentVehiclePosition);
        setShouldShowVehicle(true); // ✅ SHOW real vehicle marker
        return;
      }

      // ✅ If completely off-route and no last valid → show at pickup
      console.log("🚏 Vehicle off-route with no history, showing at pickup");
      setDisplayVehiclePosition(pickupPosition);
      setShouldShowVehicle(true);
      return;
    }

    // ✅ PRIORITY 3: Lost connection after having data → keep last valid position
    if (!currentVehiclePosition && hasReceivedAnyData && lastValidPosition) {
      console.log("📡 Connection lost, showing last known position");
      setDisplayVehiclePosition(lastValidPosition);
      setShouldShowVehicle(true);
      return;
    }

    // ✅ FALLBACK: Show at pickup
    console.log("🚏 Fallback: showing at pickup");
    setDisplayVehiclePosition(pickupPosition);
    setShouldShowVehicle(true);
  }, [
    currentVehiclePosition,
    journeyPhase,
    mapStops,
    mapRoutes,
    isOnRoute,
    lastValidPosition,
    hasReceivedAnyData,
    hasResolvedRouteDirection,
    isDirectionResolved,
  ]);

  // 📊 Calculate next stop, distance, and ETA based on journey phase
  const { nextStop, distance, eta, statusText } = useMemo(() => {
    // Better handling when data is not available
    if (
      !currentVehiclePosition ||
      !mapStops ||
      mapStops.length === 0
    ) {
      return {
        nextStop: null,
        distance: "Calculating...",
        eta: "---",
        statusText: "Loading...",
      };
    }

    const pickupStop = mapStops.find((stop) => stop.type === "pickup");
    const dropStop = mapStops.find((stop) => stop.type === "drop");

    if (!pickupStop) {
      return {
        nextStop: null,
        distance: "No stops found",
        eta: "---",
        statusText: "Setup Required",
      };
    }

    let targetStop,
      distanceToStop,
      calculatedETA,
      displayDistance,
      displayETA,
      status;
    const getDistanceMetrics = (stop) => {
      const directDistanceMeters = calculateDistance(currentVehiclePosition, stop);
      const routeDistanceKm = calculateRouteDistanceKm(
        currentVehiclePosition,
        stop,
        sortedRouteCoordinates,
      );
      const distanceKm =
        routeDistanceKm ??
        (isFinite(directDistanceMeters) ? directDistanceMeters / 1000 : null);

      if (distanceKm == null) {
        return null;
      }

      return {
        directDistanceMeters,
        distanceKm,
        eta: calculateParentETA(distanceKm, primaryVehicleDeviceId),
      };
    };

    switch (journeyPhase) {
      case JOURNEY_PHASE.GOING_TO_PICKUP:
        // Show "Arriving to pickup point" instead of distance/ETA
        targetStop = pickupStop;
        displayDistance = "Arriving to pickup point";
        displayETA = null;
        status = "Coming to Pickup";
        break;

      case JOURNEY_PHASE.GOING_TO_SCHOOL: {
        // Morning: After pickup, going to school - SHOW VEHICLE
        if (dropStop) {
          targetStop = dropStop;
          const metrics = getDistanceMetrics(dropStop);

          if (!metrics) {
            return {
              nextStop: dropStop,
              distance: "Calculating...",
              eta: "---",
              statusText: "Reaching School at",
            };
          }

          distanceToStop = metrics.directDistanceMeters;
          calculatedETA = metrics.eta.smoothedETA * 60;

          const isArrivedAtSchool =
            distanceToStop < CONSTANTS.ARRIVAL_DISTANCE &&
            metrics.eta.etaSeconds <= CONSTANTS.ARRIVAL_TIME;

          displayDistance = isArrivedAtSchool
            ? "Arrived"
            : metrics.distanceKm.toFixed(1);
          displayETA = isArrivedAtSchool
            ? "Arrived"
            : formatETAAsTime(calculatedETA);
          status = "Reaching School at";
        } else {
          return {
            nextStop: null,
            distance: "School not set",
            eta: "---",
            statusText: "Setup Required",
          };
        }
        break;
      }

      case JOURNEY_PHASE.REACHED_SCHOOL:
        // Student reached school
        targetStop = dropStop;
        displayDistance = "Reached School";
        displayETA = null;
        status = "At School";
        break;

      case JOURNEY_PHASE.GOING_TO_DROP: {
        // Afternoon: Coming back from school to home - SHOW VEHICLE
        targetStop = pickupStop;
        const metrics = getDistanceMetrics(pickupStop);

        if (!metrics) {
          return {
            nextStop: pickupStop,
            distance: "Calculating...",
            eta: "---",
            statusText: "Reaching Home at",
          };
        }

        distanceToStop = metrics.directDistanceMeters;
        calculatedETA = metrics.eta.smoothedETA * 60;

        const isArrivedAtHome =
          distanceToStop < CONSTANTS.ARRIVAL_DISTANCE &&
          metrics.eta.etaSeconds <= CONSTANTS.ARRIVAL_TIME;

        displayDistance = isArrivedAtHome
          ? "Arrived"
          : metrics.distanceKm.toFixed(1);
        displayETA = isArrivedAtHome
          ? "Arrived"
          : formatETAAsTime(calculatedETA);
        status = "Reaching Home at";
        break;
      }

      case JOURNEY_PHASE.STUDENT_DROPPED:
        // Student has been dropped
        targetStop = pickupStop;
        displayDistance = "Student Dropped at Home";
        displayETA = null;
        status = "Journey Complete";
        break;

      case JOURNEY_PHASE.UNKNOWN:
      default:
        // Show "Calculating..." when phase is unknown
        return {
          nextStop: null,
          distance: "Calculating...",
          eta: "---",
          statusText: "Please wait...",
        };
    }

    return {
      nextStop: targetStop,
      distance: displayDistance,
      eta: displayETA,
      statusText: status,
    };
  }, [
    calculateParentETA,
    currentVehiclePosition,
    journeyPhase,
    mapStops,
    primaryVehicleDeviceId,
    sortedRouteCoordinates,
  ]);

  // Update vehicle position from ParentViewMap
  const handleVehiclePositionUpdate = (position) => {
    setCurrentVehiclePosition(position);
  };

  const handleSOSClick = () => {
    console.log("SOS button clicked");
    const schoolEmergencyNumber = "1234567890";
    window.location.href = `tel:${schoolEmergencyNumber}`;
  };

  return (
    <Box sx={styles.mainContainer}>
      <Box sx={styles.mapWrapper}>
        <ParentViewMap
          stops={mapStops}
          routes={mapRoutes}
          vehicles={mapVehicles}
          studentName={studentInfo.name}
          studentIcon={studentInfo.icon}
          onVehiclePositionUpdate={handleVehiclePositionUpdate}
          shouldShowVehicle={shouldShowVehicle}
          displayVehiclePosition={displayVehiclePosition}
          journeyPhase={journeyPhase}
          vehicleRouteDirection={effectiveVehicleRouteDirection}
        />
      </Box>
      {/* Header with Distance and ETA info */}
      <Box sx={styles.headerContainer}>
        <Box sx={styles.headerBackground}>
          <Box sx={styles.headerContent}>
            {journeyPhase === JOURNEY_PHASE.GOING_TO_PICKUP ||
            journeyPhase === JOURNEY_PHASE.STUDENT_DROPPED ||
            journeyPhase === JOURNEY_PHASE.REACHED_SCHOOL ? (
              // Special display for single column status
              <Box sx={styles.specialStatusContainer}>
                <span style={styles.specialStatusText}>{distance}</span>
              </Box>
            ) : (
              <>
                <Box sx={styles.distanceContainer}>
                  <span style={styles.metricLabel}>Distance Left</span>
                  <span style={styles.metricValue}>
                    {distance === "Arrived"
                      ? "Arrived"
                      : distance === "Calculating..." ||
                          distance === "School not set" ||
                          distance === "No stops found"
                        ? distance
                        : `${distance} km`}
                  </span>
                </Box>
                <Box sx={styles.etaContainer}>
                  <span style={styles.metricLabel}>{statusText}</span>
                  <span style={styles.metricValue}>{eta || "---"}</span>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>
      {/* SOS Button */}
      <Box sx={styles.sosButtonContainer}>
        <Box component="button" onClick={handleSOSClick} sx={styles.sosButton}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M17.0727 14.2835L13.6852 11.2035C13.5251 11.0579 13.3147 10.9803 13.0984 10.987C12.8821 10.9937 12.6769 11.0841 12.5261 11.2393L10.5319 13.2901C10.0519 13.1985 9.0869 12.8976 8.09357 11.9068C7.10023 10.9126 6.7994 9.94512 6.71024 9.46845L8.7594 7.47345C8.91474 7.32272 9.00535 7.11747 9.01204 6.90112C9.01872 6.68478 8.94097 6.47432 8.79524 6.31428L5.71607 2.92762C5.57027 2.76708 5.36763 2.6697 5.1512 2.65617C4.93476 2.64263 4.72157 2.714 4.5569 2.85512L2.74857 4.40595C2.60449 4.55055 2.5185 4.74299 2.5069 4.94678C2.4944 5.15512 2.25607 10.0901 6.08274 13.9185C9.42107 17.256 13.6027 17.5001 14.7544 17.5001C14.9227 17.5001 15.0261 17.4951 15.0536 17.4935C15.2573 17.482 15.4497 17.3957 15.5936 17.251L17.1436 15.4418C17.2852 15.2776 17.3571 15.0646 17.3439 14.8482C17.3307 14.6318 17.2334 14.4291 17.0727 14.2835Z"
              fill="white"
            />
          </svg>
          <span style={styles.sosText}>SOS</span>
        </Box>
      </Box>
    </Box>
  );
}
